// =============================================================================
// domain/education.ts — the in-product explainers.
// -----------------------------------------------------------------------------
// Two tiers behind one `?` mark, exactly as the old client has it:
//
//   the TIP      one or two sentences, on hover
//   the ARTICLE  the full explainer, in a slide-out drawer, on click
//
// ── ⚠ THIS IS COPY, AND IT IS PORTED VERBATIM ──
//
// The articles were written for the old client and are relocated unchanged, with
// ONE exception, marked in place: `coach.method` told the reader that "excluded
// and archived accounts are left out", which is no longer true here — archiving
// is not a filter in this client (D111/PD-11), on Josh's own instruction. A help
// article that describes a different rule from the one the code applies is worse
// than no article: it teaches the trader to distrust a number that is correct.
//
// ⚠ ANY OTHER SENTENCE THAT STOPS BEING TRUE HAS TO BE FIXED HERE TOO. These are
// prose claims about behaviour and nothing mechanical can check them — the tests
// pin the registry's SHAPE and the one sentence above, not its accuracy.
//
// ── ⚠ THEY COME ACROSS WITH THEIR TABS ──
//
// The old client's registry holds explainers for Analytics, Excursion, the
// Calendar, Practice and the Dashboard as well. Those tabs are not ported, and a
// mark whose article describes a screen that does not exist is a broken promise.
// They come across with their tabs.
//
// ✅ FIRST TIME THAT RULE PAID OUT, 2026-09-29: the Prop Report is ported, so its
// two applicable articles moved across with it (TCR-17). This block used to read
// "COACH-ONLY, FOR NOW" — it is no longer coach-only, and the rule, not the
// inventory, is the part that was load-bearing.
//
// ⚠ Its THIRD mark did not come: beta marks its Risk of Ruin card, which is not
// built here. The reason is recorded at the foot of the registry, beside where
// the entry would go — not here, because that is where somebody adding it looks.
// =============================================================================

export interface EduEntry {
  /** The drawer's heading, and the tooltip's. */
  title: string;
  /** One or two sentences. Shown on hover, and it must stand alone. */
  tip: string;
  /** Markdown. Absent means the mark is a tooltip only, never a button. */
  article?: string;
}

export const EDUCATION: Readonly<Record<string, EduEntry>> = {
  'coach.method': {
    title: 'Spot it. Fix it. Prove it.',
    tip: 'The whole loop: find your costliest habits, write a rule for each, prove each one moved its number. Adherence and outcome judged separately.',
    article: `
# Spot it. Fix it. Prove it.

Every other journal is a rear-view mirror — it shows you what happened. This closes the loop: find the one habit costing you the most, commit to a single rule, and prove the rule actually moved the number. It reads your own trades across every account except the ones you have marked **exclude from metrics** — your SIM, replay and demo accounts. Archiving an account tidies it off your list; it does not hide it from these numbers, because the trades were real.

## The three acts

- **Spot it** — your leaks, ranked by dollars, biggest first. *Money you lost* is realized: already booked, symmetric, fixable with a rule. *Money you left* is a counterfactual ceiling — never ranked against realized losses, because it is not the same money.
- **Fix it** — turn a leak into ONE concrete, testable rule. You can write a rule per leak and run several at once; each freezes two numbers of its own: that leak's baseline and your expectancy at the time. You cannot move the goalposts later.
- **Prove it** — for each rule, the loop re-measures the exact same number over the trades since you started it, and judges two things separately: did you follow the rule, and did the number move. Worked → bank the habit. Didn't → sharpen it. Never followed → it says so, instead of blaming the rule.

## One rule per leak

Keep each rule tied to a single leak and specific enough to grade trade by trade — that is what makes a verdict mean something. Run as many as you will genuinely work at once; each proves on its own frozen baseline, so they never blur together. What kills progress is not the number of rules — it is a vague one ("trade better") you cannot prove either way. A rule you actually held for 30 trades beats five you half-followed for a week, so start with the leaks that cost you the most and only take on what you will really track.

## Adherence vs outcome

The most common false conclusion in trading is "that rule didn't work" when the truth is "I never followed it." Prove it grades those independently. Mark whether you held each rule on each trade, with the ✓ / ✗ / — control in that rule's Prove-it review list. No marks, no verdict — because an outcome you didn't actually test tells you nothing.

## The expectancy guardrail

No rule is certified on its own number alone. A rule can improve its target metric while quietly bleeding your edge elsewhere — "cut every trade at -1R" crushes give-back and your P&L with it. So Fix it also freezes your expectancy, and Prove it refuses to call a rule **Worked** if your per-trade expectancy degraded while you followed it.

## When it can't help you yet

Early, it holds back on purpose. A verdict needs enough trades since you froze the baseline to mean anything, so it sits at *too soon* rather than calling a good rule dead on three trades. And a leak it cannot measure — no qualifying trades, or no data feed — reads *needs data*, never a false *no leak here*.
`,
  },

  'coach.leak.hold-disposition': {
    title: 'Riding losers, cutting winners',
    tip: 'The disposition effect, straight from your fill timestamps: do you hold losers longer than winners? Time-based — it never reads your stop or risk.',
    article: `
# Riding losers, cutting winners

The disposition effect — the human reflex to bank profits quickly and give losers "room to come back." This leak measures it directly from your fill timestamps. It reads no stops, no risk settings, no chart data — only how long you held, and whether the trade won or lost.

## What it measures

Two numbers:

- **The gap** (the headline, in minutes) — the median hold time of your LOSERS minus the median hold time of your WINNERS. A positive gap means you sit in losers longer than winners: the disposition effect, quantified.
- **The dollar figure** — the booked loss on the specific losers you held *past your typical winner's hold time* (the "ridden losers"). Those are the trades the review list shows.

**Nothing is counted unless the gap is positive.** If your losers' median hold is the same as your winners' or shorter, the dollar figure is zero, the review list is empty, and the card reads as no leak — because on your own fills, it is not one. The gap still shows, negative and all, so you can see by how much you are doing the opposite.

## How a trade lands on the list

First, the gap has to point at riding at all. **If your losers' median hold is not longer than your winners', no trade lands on the list** — however long any individual loser ran. There is no habit to review, so there is nothing to review.

When the gap *is* positive, a trade is a ridden loser if all three are true:

1. It **lost money**.
2. It has **both an entry and an exit timestamp**, so hold time is computable.
3. You **held it longer than the median hold time of your winners**.

That is the entire test. It never looks at your stop, your R, or your risk plan — which is why a trade with no risk management set can still qualify: if it lost and you sat in it longer than you would hold a winner, it is a ridden loser. A trade with no stop is in fact the textbook case — no mechanical exit means you hold and hope, exactly the behavior this hunts. And the **loss size does not decide inclusion**: a small 5-point loss held long counts the same as a large one; size only changes how much it adds to the dollar total.

## How to read it

The **Held vs Xm** column in the review drilldown makes it self-evident — X is your winner median, and every row's hold time sits above it. Open a few and read the tape: was the trade drifting sideways in the red while you waited, or genuinely building a case to stay in?

## What to do about it

The fix is a time rule, not a stop tweak: *if a trade has not worked within [your winner-median] minutes, it is not working — flatten it.* Then Prove it re-measures your gap over the next trades. If cutting the ride-time also holds your expectancy (the guardrail), you have banked a real habit. If the losers you cut were about to turn, expectancy drops and the guardrail catches it — that is the honest test.

## When this card lies

- **It is behavioral, not causal.** The dollar figure is the loss *associated with* riding losers, not a promise that exiting sooner would have saved that exact amount — there is no excursion behind this leak, only fills and clocks.
- **It cannot read intent.** A loser held long on a deliberately wide stop looks identical to one held long in denial. The metric flags candidates; you judge them — that is what the review list is for.
- **Small samples.** With fewer than 5 winners or 5 losers that carry timestamps, the medians are noisy. It abstains until there is enough to compare.
`,
  },

  'coach.leak.controllable-loss': {
    title: 'Controllable losses',
    tip: 'Losses you tagged as your own doing — revenge, FOMO, overtrading, a flagged mistake. The one leak fully inside your control.',
    article: `
# Controllable losses

The losses you booked that were not the market's fault — the ones you tagged as revenge, FOMO, overtrading, or a mistake. Every other leak is a matter of degree; this one is binary and fully inside your control. It is the first place to look, because closing it needs no edge, no better read, and no data feed — only discipline.

## What it measures

The total dollars booked on trades you flagged as a controllable loss over the period. It reads your own tags — the revenge / FOMO / overtraded flags and any mistake tag you marked controllable — so it is exactly as honest as your journaling. A clean, well-tagged book with zero controllable losses ranks $0 here, and that is the goal.

## How to read it

The **Reason** column in the review drilldown shows what made each trade controllable. Open the list and look for a pattern: three revenge re-entries after the same red session is a different fix than five FOMO chases into extended moves. The dollar figure is real, booked money — not a counterfactual — so a rule that cuts it wins that money back directly.

## What to do about it

Target the most common reason with one hard rule. Revenge → no re-entry within N minutes of a loss. FOMO → no entry more than X ticks from your trigger. Overtrading → a hard trade cap, or "two strikes and I am flat for the session." Then Prove it re-measures the same dollars over your next trades — and because this is realized money, an honest drop is unambiguous.

## When this card lies

It only sees what you tag. An untagged controllable loss is invisible here — and "reviewed and clean" and "never looked" are identical in the data, both just the absence of a tag. So the number is a floor, not a ceiling. If it reads $0 but you know better, the fix is honest tagging first, not a new rule.
`,
  },

  'coach.leak.tilt-after-loss': {
    title: 'Tilt — win-rate drop after a loss',
    tip: 'How much your win rate drops on the very next setup once you are red. A GAP between after-win and after-loss — it isolates tilt from general edge.',
    article: `
# Tilt — win-rate drop after a loss

Whether a loss knocks you off your game. It compares how you trade right after a WIN versus right after a LOSS — the drop between the two is the tilt signal. Measuring the gap, not the raw after-loss win rate, is deliberate: a low after-loss win rate could just mean your edge is thin everywhere. The gap isolates the part that is actually about the loss.

## What it measures

Your win rate on the trade immediately following a winner, minus your win rate on the trade immediately following a loser. A positive gap means you trade worse after a red trade — the tilt tax. Scratches are skipped so they never break the win/loss chain. The dollar figure is the booked loss on your after-loss trades when that sequence ran net negative.

## How to read it

A gap near zero means losses do not rattle you — no rule needed here (often true even for traders who assume they tilt). A meaningful gap means the loss itself is degrading the next decision. Read it alongside the sequence: are the after-loss losers revenge entries, over-sized, or just ordinary trades taken in a worse headspace?

## What to do about it

The fix is a circuit-breaker on the next trade after a loss: cut size in half, wait a fixed cooldown, or require a higher-grade setup before re-engaging. Then Prove it re-measures the gap. The expectancy guardrail matters here — a rule that only skips trades can shrink the gap while also skipping winners, so the guardrail checks your edge held.

## When this card lies

- **It is a rate, so small samples wobble.** You need a decent count of both after-win and after-loss trades before the gap means anything.
- **A green after-loss book is not tilt.** If your after-loss trades are net positive, there is no realized leak to fix — chasing a gap that costs nothing invents a problem.
- **It sees sequence, not cause.** A cluster of after-loss losers on one bad day can create a gap that is not a stable habit. The review list is there to judge that.
`,
  },

  'coach.leak.add-to-loser': {
    title: 'Adding to losers',
    tip: 'Averaging down — adding at a worse price while underwater, read from your fills. Pyramiding UP into a winner is not flagged.',
    article: `
# Adding to losers

Averaging down — adding to a position at a price worse than your running average while the trade is underwater, pulling your basis the wrong way. It is read straight from your fills, so it needs no chart data. Adding at a BETTER price — pyramiding into a winner — is deliberately not flagged; that is a different decision entirely.

## What it measures

Across the trades where you have fills, the share where you added a same-direction entry at an adverse price (for a long, below your average; for a short, above it). The dollar figure is the booked loss on the averaged-down trades that LOST — the won ones cost you nothing, so they do not count against you here.

## How to read it

This is a yes/no behavior per trade, so the signal is the RATE — how often you average down — and the dollars are the damage on the ones that did not come back. Open the review list, then open a few of the trades and read their fills: were the adds planned scale-ins to a defined level, or reactive "it has to bounce" adds with no plan?

## What to do about it

The rule is usually a hard line, not a nuance: no adds below (long) or above (short) your average — if the first entry is wrong, take the stop, do not finance it. If you legitimately scale into a level, the rule becomes "adds only at pre-defined levels, sized into the original plan." Prove it then re-measures the rate, and the guardrail confirms you did not just start cutting winners to compensate.

## When this card lies

- **No fills, no read.** Trades without logged executions are excluded — the rate is honest only over what it can see.
- **It cannot see intent.** A disciplined scale-in at support and a panic add look similar in price alone; the metric flags the pattern, you confirm which it was.
- **A book that only pyramids up ranks $0** — correctly. Adding to strength is not this leak.
`,
  },

  'coach.leak.premature-exit': {
    title: 'Cost of premature exit',
    tip: 'Favorable runs on exits that never hit your stop, MINUS the cost of the ones that would have stopped out. An upper bound — never a hold-longer rule.',
    article: `
# Cost of premature exit

What you might have captured by holding longer — told honestly. For each exit it looks at the run that followed, but it does NOT simply sum the good outcomes: it credits the favorable continuation on exits that never touched your stop, and SUBTRACTS the stop-loss you would have eaten on the ones that reversed. The net is the only figure that is not a fantasy.

## What it measures

Per trade, the favorable move after your exit (on trades that would have survived) minus the hold-to-stop cost (on trades that would have been stopped). It needs post-exit price data — the one leak that depends on a chart or market-data feed. The **Ran after** column in the review list shows, for each qualifying exit, how much that continuation was worth.

## Where the post-exit run comes from

It is not measured as you read this. Walking the seconds after every exit costs money, so it runs on demand in the other client — the **Recompute** button on Trade Extension (Analytics → Excursion) — and what it leaves behind is what this reads. **A trade whose run was never computed simply is not in the sample**; it never counts as a zero, so the leak reads "not enough data" rather than "your exits are perfect."

## How to read it — and the trap

This is money you LEFT, not money you lost. It is an upper bound, and it is the single most dangerous number in the report if read naively. On real books, the large majority of early exits are *protective* — holding would have hit the stop. That is already netted off the figure you see: the stop-outs are subtracted, not ignored, which is why the number can even come out **negative** — meaning holding longer would have cost you and there is no leak here at all.

## What to do about it

Never write "hold longer." The only rule that reaches this upside is CONDITIONAL: trail the stop once price runs 1R, or hold only under a specific setup or confluence that separates the runners from the head-fakes. Then Prove it — and the expectancy guardrail — adjudicate whether the condition actually caught the winners without giving back the protection.

## When this card lies

It assumes a peak exit on the favorable side and a full stop on the adverse side, so both legs are bounds, not promises. Small samples and missing extension data widen the error. Treat it as "is there anything here worth a conditional rule?" — never as recoverable P&L.
`,
  },

  'coach.leak.mfe-capture': {
    title: 'Capture efficiency',
    tip: 'The share of the peak favorable move you kept, on winners. The % is the real signal; the give-back $ is a ceiling, not recoverable money.',
    article: `
# Capture efficiency

On your winning trades, how much of the peak favorable move you actually walked away with. If a trade ran 10 points your way and you booked 4, you captured 40%. It reads your excursion (MFE) data — from the loggers, an excursion-providing broker, or a market-data provider — so it is available wherever any of those recorded the move.

## What it measures

The median capture percentage across your winners (peak favorable move vs what you kept). **The percentage is the whole of it here** — the companion give-back dollar figure is not computed in this client yet, and its row says so rather than printing a zero. That is no great loss: the percentage was always the honest half (see below).

## How to read it

Low capture — say under 50% — means you are leaving a lot of each winner on the table, an exit-timing issue rather than an entry one. High capture means you are getting most of the move and there is little to chase. Slice it by pattern: capture is often fine on scalps and poor on trend setups, and the fix differs.

## What to do about it

A capture rule is about the exit: trail rather than take a fixed target, scale out in thirds, or hold a runner to a structure level on setups that trend. Because "hold for more" can convert winners into round-trips, Prove it leans on the expectancy guardrail — capture should rise without your win rate or expectancy falling.

## Pairs with

The **MFE Capture** card on Analytics → Excursion shows the same measure with its distribution behind it.

## When this card lies

Give-back **dollars** — wherever you meet them — are structural, not recoverable: MFE is the maximum, nobody exits at the exact peak, so the figure is positive by construction and will always look large. The capture **percentage** is the honest metric, and it is the one this row reports. Thin excursion coverage also makes early numbers noisy — the more trades carry MFE data, the more it solidifies.
`,
  },

  'coach.leak.plan-shortfall': {
    title: 'Cutting winners short of plan',
    tip: 'On your winners, how much of the target YOU set you actually captured. Grounded in your logged plan — no chart data needed.',
    article: `
# Cutting winners short of plan

On your winning trades, how much of the reward you PLANNED you actually captured. If you logged a 3R target and booked 1.5R, you captured half of your own plan. Unlike the excursion leaks, this is grounded in the intent you recorded — not a market peak — so it needs no chart data, only your logged R:R.

## What it measures

Across eligible winners (those with a logged planned R:R), the median capture of your planned reward — realized R divided by planned R, capped at 100% (you cannot over-capture your own plan). The dollar figure is the shortfall in R times your dollar risk, summed — what you left versus the targets you set. The **Planned R** column in the review list shows each trade's target; read it against the realized-R column beside it.

## How to read it

Consistently taking half your planned target is one of two things, and both are coachable: you are cutting winners early out of fear, or you are setting targets you do not actually believe in. The review list tells you which — if the trades were still trending when you exited, it is the former; if they stalled well short every time, your targets are fiction.

## What to do about it

If you are cutting good trades: a partial-exit rule that banks some and lets the rest run to your logged target. If your targets are unrealistic: fix the plan, not the exit — log targets you will actually hold for. Prove it re-measures your capture, and the guardrail guards against "hold to target" quietly turning winners into losers.

## When this card lies

- **It trusts your logged plan.** Garbage targets in, garbage shortfall out — this only works if your R:R reflects a real intention.
- **It is an upper bound.** Hitting your target was not guaranteed just because you set it; without excursion we cannot know the move was there. That is why any "hold to target" rule must clear the expectancy guardrail.
- **No plan, no read.** Trades with no logged R:R are excluded, so the rate is honest over the trades you actually planned.
`,
  },

  // ── Coach's Report — the three acts ─────────────────────────────────────────
  'coach.spot': {
    title: 'Spot it — your ranked leaks',
    tip: 'The diagnosis: every measurable leak ranked by the dollars it costs you, biggest first. Realized losses and opportunity ceilings, never mixed.',
    article: `
# Spot it

The diagnosis. Every leak the loop can measure, ranked by the dollars it costs you, biggest first — so you always know what is worth fixing next. You can write a rule for each leak; this ranking tells you which to take on first.

## Two lists, never merged

- **Money you lost** — realized leaks. Dollars already booked against you: controllable losses, tilt, riding losers, averaging down. A rule that cuts them wins that money back directly.
- **Money you left** — opportunity leaks. A counterfactual ceiling: what a different exit *might* have been worth (premature exit, capture, plan shortfall). Never ranked against realized losses, because it is not the same money — an upper bound on opportunity, not booked P and L.

"Start here" always points at your top REALIZED leak, however large an opportunity number looks. Realized money is the safe thing to chase first; opportunity numbers need a conditional rule and a guardrail before they mean anything.

## How to read it

Each row shows the dollars, the sample it is drawn from, and a **review** link that opens the exact trades behind the number — so you can see the behavior before you write a rule for it. A leak the loop cannot measure reads "needs data," never a false "no leak here."

## What to do about it

Pick a realized leak and move to Fix it — start with the one costing you the most. You can write a rule for each leak and run them at once; because each proves on its own frozen baseline, a change to any one rule is still cleanly attributable. Take on only as many as you will actually track.

## When this card lies

Rankings move with your sample. A thin period puts noise at the top; a leak flagged "thin sample" has not earned the top slot yet. And realized rankings are only as honest as your tagging — each leak card says where its number comes from.
`,
  },

  'coach.fix': {
    title: 'Fix it — one rule, frozen',
    tip: 'Turn a leak into ONE testable rule — one per leak, run as many as you will work. Saving it freezes that leak\'s baseline and your expectancy so Prove it compares like with like.',
    article: `
# Fix it

Turn the leak you picked into ONE concrete, testable rule — one rule per leak, and you can run several at once. Not a resolution ("trade better") and not one rule trying to fix everything — a single instruction, tied to this leak, specific enough that trade by trade you can say whether you followed it.

## What happens when you save

Saving the rule freezes two numbers, both from the same look-back window:

- **The baseline** — the leak's current value. Prove it re-measures this exact number over the trades since, so you compare like with like.
- **Your expectancy** — your net dollars per trade right now. This is the guardrail: a rule that improves its target metric while your expectancy falls has not actually helped you.

Freezing at Fix time is the point — you cannot move the goalposts later, and Prove it measures against what was true when you committed.

## Write a rule you can grade

The best rules are mechanical: "no re-entry within 10 minutes of a loss," "trail the stop to break-even at 1R," "half size on the trade after a red one." Vague rules cannot be proven, because you cannot honestly mark whether you followed them.

## Opportunity leaks need a conditional rule

If you are fixing a "money you left" leak, the loop warns you: never write a blanket "hold longer." Those numbers are one-sided upper bounds, and holding everything longer pays for the runners with the stop-outs. The only rule that reaches the upside is conditional — trail the stop, or hold only under a specific setup.

## When this refuses you

If the baseline is unusable — too few trades, or already at the best attainable value — Fix it will not let you freeze it, and it tells you why. A rule pinned to an unimprovable baseline could never be proven, so the refusal is the lesson: pick a different leak or a longer window.
`,
  },

  'coach.prove': {
    title: 'Prove it — did the rule work',
    tip: 'Re-measure the exact number you froze, and judge two things separately: did you follow the rule, and did the number move.',
    article: `
# Prove it

The part every other journal skips. Once a rule is live, this re-measures the exact number you froze at Fix time over the trades since — and judges two things separately: did you follow the rule, and did the number move.

## The two questions, never conflated

- **Adherence** — did you actually hold the rule? Measured only from the trades you explicitly marked. If you did not follow it, the outcome cannot be trusted either way.
- **Outcome** — did the leak's number improve versus its frozen baseline?

Separating them kills the single most common false conclusion in trading: "that rule did not work," when the truth is "I never followed it."

## The verdict

The badge reads the ladder — from "no baseline" and "too soon" (not enough evidence yet), through "not followed" and "guardrail" (the rule failed a precondition), to "worked" or "didn't work." A rule only reaches **worked** if you followed it, the number improved, and your expectancy held.

## What you do with it

- **Worked** → bank it as a proven habit; your other rules keep running.
- **Didn't work** → sharpen the rule and keep testing.
- **Too soon** → keep going; the sample is not there yet.

Banked and retired rules become your ledger of tested hypotheses — so you never re-litigate a settled question.

## When this card lies

Proof is weight of evidence over enough trades, not one green week. Early on it holds at "too soon" on purpose. And a verdict is only as honest as your adherence marks — an unmarked rule stays "unmeasured," because a result you never tested tells you nothing.
`,
  },

  // ── Coach's Report — Prove mechanics ────────────────────────────────────────
  'coach.verdict-ladder': {
    title: 'The verdict ladder',
    tip: 'The badge is checked in a deliberate order — the first failure that applies wins, because some failures make the rest meaningless.',
    article: `
# The verdict ladder

The badge on the Prove card is not a simple pass or fail. Verdicts are checked in a deliberate order, and the first one that applies wins — because some failures make the others meaningless.

## The order, and why it matters

1. **No baseline** — the rule was frozen against an unusable number (too thin, or already at its floor). Nothing downstream can be trusted, so this comes first.
2. **Too soon** — not enough trades since you started to say anything. The loop refuses to grade a rule on three trades.
3. **Unmeasured** — enough trades, but too few are marked for adherence. Without knowing whether you followed the rule, the outcome is uninterpretable.
4. **Not followed** — you were marked, and you mostly broke the rule. The verdict is on you, not the rule — it was never really tested.
5. **Guardrail** — you followed it and the metric moved, but your expectancy degraded while you did. The rule "worked" on its own number and quietly cost you money.
6. **Worked / Didn't work** — the only honest outcomes, reached only after everything above is clear.

## Why it is a ladder, not a scorecard

A rule you never followed cannot "not work" — that would blame the rule for your discipline. A rule that tanks your expectancy cannot have "worked" just because its narrow metric improved. Ordering the checks is what keeps the verdict from lying to you.

## When this card lies

The ladder is only as good as its inputs: honest adherence marks and enough sample. It will sit at "too soon" or "unmeasured" rather than guess — that is a feature, not a stall.
`,
  },

  'coach.guardrail': {
    title: 'The expectancy guardrail',
    tip: 'A second, independent check: did your net-per-trade edge hold while you followed the rule? Stops a narrow metric from being gamed.',
    article: `
# The expectancy guardrail

No rule is ever certified on its own number alone. The guardrail is a second, independent check: did your actual edge — net dollars per trade — hold while you followed the rule?

## Why it exists

Almost any single metric can be gamed by a rule that hurts you elsewhere. "Cut every trade at minus 1R" crushes your give-back number and your P and L with it. "Never trade after a loss" flattens your tilt gap by skipping perfectly good trades. Each would show a "better" target metric while your account bled. The guardrail catches exactly that.

## How it works

At Fix time the loop freezes your expectancy alongside the leak's baseline — same window, same trades. At Prove time it re-measures. If the rule improved its target metric but your per-trade expectancy fell beyond a small tolerance, the verdict reads **Guardrail**, not Worked — even if the number you were chasing looks great.

## Reading it

The guardrail row shows your baseline expectancy moving to your current expectancy. It is scaled by the size of the baseline so a losing book stays honest — a move from minus 10 dollars to minus 5 dollars a trade is a real 50% improvement. If no expectancy baseline was captured, it refuses to certify rather than assume you are fine.

## When this card lies

It needs a meaningful sample on both sides to trust the comparison. On thin samples it is conservative — it would rather withhold a "Worked" than hand you a false one.
`,
  },

  'coach.adherence': {
    title: 'Adherence — did you follow it?',
    tip: 'Did you actually hold the rule? Counted only from trades you mark. Without it, every verdict is stuck at unmeasured.',
    article: `
# Adherence — did you follow it?

The single most important measurement in Prove it, and the one only you can supply. On each trade while a rule is live, you mark whether you held it: followed, broke it, or not judged (the rule did not apply).

## Why it cannot be inferred

The loop can measure your leak from your trades, but it cannot read your intent. A trade that happened to avoid the leaky behavior is not the same as you deliberately following the rule — and silence is ambiguous: "I reviewed this and the rule did not apply" and "I never looked" are identical in the data. So adherence is counted ONLY from trades you explicitly marked.

## Why it is separate from outcome

Adherence and outcome answer different questions — did you do the thing, and did the thing help. Keeping them apart is what lets Prove it say "you did not actually follow this" instead of falsely concluding "this rule does not work." Without your marks, every verdict is stuck at "unmeasured."

## How it feeds the verdict

The loop needs a minimum number of marked trades before it grades adherence at all, and a minimum share held before a rule counts as followed. Below those it reports "unmeasured" rather than guess. The mark is pinned to the specific rule, so banking one rule and starting the next never silently re-interprets your old marks.

## When this card lies

Only if your marks are dishonest. Marking followed on trades you actually fudged does not fool the guardrail — your expectancy still tells the truth — but it robs you of the one signal the loop cannot reconstruct. Mark it straight.
`,
  },

  // ── Coach's Report — the four dimensions (lenses beyond the leaks) ───────────
  'coach.dimension.risk-of-ruin': {
    title: 'Risk of Ruin',
    tip: 'The odds of breaching your trailing-drawdown floor over the next 30 days — a Monte-Carlo resample of your own daily P and L, not a gut feel.',
    article: `
# Risk of Ruin

The probability that this account breaches its trailing-drawdown floor over the next 30 trading days — the prop-account failure you actually care about, expressed as a single percentage.

## How it is computed

Not the textbook formula. It takes this account's realized daily P and L, resamples it into 1,500 simulated forward paths of 30 days each, and counts how many hit the drawdown floor. Because it draws from YOUR distribution — your real up days and down days — it captures the fat tails and streakiness that a clean win-rate formula misses.

## How to read it

- **Under 5%** — comfortable; you have room to press your edge.
- **5 to 15%** — manageable, but the give-back on losers is the lever, not position size.
- **Over 15%** — elevated; protect the downside before you press.

Watch the trend across periods more than the reading on any single day — a handful of fluke losers can spike it temporarily.

## What to do about it

When it climbs, the fix is almost always the loss side, not the win side: tighten the give-back on losers, cut size, or build cushion by skipping marginal trades. It is a risk gauge, so the response is defensive.

## Pairs with

Your account's drawdown limit and what is left of its cushion sit on the **Accounts** tab — and the panel needs a max drawdown set there before it can run at all, because without a floor there is nothing to breach.

## When this card lies

It publishes a number from as few as 2 trading days so you can watch it develop — but a resample of only a handful of daily results is a rough indication, flagged as a small sample until it settles (around 20+ trading days). It also assumes your recent trading is representative of the next 30 days; a strategy change or a regime shift makes the history a weaker guide, so read it as a current-state probability, not a forecast.
`,
  },

  'coach.dimension.capture': {
    title: 'Excursion · MFE capture',
    tip: 'The share of the favorable move you actually keep, plus your E-ratio — a read on whether your edge lives in the entry or the exit.',
    article: `
# Excursion · MFE capture

Two numbers that together say whether your edge is in getting IN or getting OUT: how much of the favorable move you capture, and your E-ratio.

## What it shows

- **Capture %** — the median share of each trade's peak favorable move that you actually booked. Low capture means you are leaving a lot on the table at the exit.
- **E-ratio** — average favorable excursion divided by average adverse excursion. Above about 1.3 is a real directional edge; near 1 is thin. It measures whether your entries put price in your favor at all.

Read together: a strong E-ratio with poor capture is an EXIT problem — your entries are good, you are just cutting winners. A weak E-ratio is an ENTRY problem no exit rule can fix.

## How to read it

The card reads "the gap is on the exit, not the entry" when capture is the weak link. That is your cue that the work is in how you exit — trailing, scaling, holding to structure — not in finding better setups.

## Pairs with

This is the percentage lens; the **Cutting winners short of plan** leak in the board above turns the same idea into dollars against your own logged target, and the **MFE Capture** card on Analytics → Excursion carries the distribution behind the median.

## When this card lies

It needs excursion data — from the loggers, an excursion-providing broker, or a market-data provider — so accounts without it read empty. Thin coverage makes the median noisy; it firms up as more trades carry MFE.
`,
  },

  'coach.dimension.confluence': {
    title: 'Confluence Edge',
    tip: 'Which of your tagged confluences actually carry your edge — ranked by the median R of trades that had them.',
    article: `
# Confluence Edge

Which of the confluences you tag actually carry your edge — so you can rank your A-grade setups by what genuinely performs, not by what feels strong.

## What it shows

For each confluence you tag on your trades, the median R-multiple of the trades that had it. The card surfaces your strongest at the top. R-multiples are used, not raw dollars, so a confluence is judged on payoff per unit of risk — comparable across instruments and size.

## How to read it

The top row is the tag most associated with your winning trades; that is the pattern to lean into and size up. A confluence that ranks near zero or negative is one you believe in but the data does not support — a candidate to drop from your A-grade checklist.

## What to do about it

Build your setup grading around what actually carries. If "prior-day level plus delta divergence" tops the board, that stack earns full size; a confluence that does not rank does not get to justify a trade on its own.

## Pairs with

Each row here carries its own sample count, and that is the number to read it against — confluence signal needs volume, so a tag on three trades is a hint, not a verdict.

## When this card lies

It only knows what you tag, and it is correlational, not causal — a confluence that co-occurs with your best sessions is not necessarily what made them work. Treat low-sample rows as noise until the count builds.
`,
  },

  'coach.dimension.pattern-exit': {
    title: 'Pattern × Exit',
    tip: 'Your best and worst pattern-plus-exit-model pairings — because the same setup can win or lose depending on how you manage it out.',
    article: `
# Pattern × Exit

The same setup can be a winner or a loser depending on how you exit it. This card pairs each pattern with each exit model and surfaces your best and worst combinations — the spread between them is often your single biggest lever.

## What it shows

For every pattern-plus-exit-model pairing with enough trades, the median R. The best pairing (green) and the worst (red) are shown together, because the gap between them is the point: it usually says the fix is the EXIT model, not the setup.

## How to read it

If your strongest pattern posts a great R under one exit model and a poor one under another, the setup is not the problem — the management is. That is a far cheaper fix than hunting for new setups: keep the entry, change how you take it off.

## What to do about it

Match your best exit model to your best pattern deliberately, and stop pairing a good setup with an exit that bleeds it. If "breakout plus fixed-target" underperforms "breakout plus trail," the rule writes itself.

## Pairs with

**Setup Quality** on the Analytics tab ranks your patterns on their own, without the exit model. Read the two together: a setup that leads there but posts a poor pairing here is being managed badly, not chosen badly. Each cell needs at least a few trades before its R is trustworthy.

## When this card lies

Pairings fragment your sample fast — split across patterns AND exit models, cell counts get small quickly, and a single outlier can crown a "best" pairing that will not repeat. Weight it by how many trades sit behind each cell.
`,
  },

  // ===========================================================================
  // The Prop Report's, which arrived WITH their tab
  // ---------------------------------------------------------------------------
  // ⚠ The header's rule is "they come across with their tabs", and the Prop
  // Report is ported — so these are due. Beta marks THREE spots on that screen.
  // The third is deliberately absent; see the note after `analytics.tilt-signal`.
  //
  // 🔴 TWO SENTENCES OF BETA'S PROSE ARE CORRECTED, both marked in place below,
  // following the `coach.method` precedent rather than inventing a new one.
  // ===========================================================================

  'analytics.prop-report': {
    title: 'Prop Report',
    tip: 'Portfolio dashboard across every prop account. Funded status + payout velocity + the bottom-line P&L of running prop accounts as a business.',
    // ⚠ CORRECTED TWICE AGAINST BETA'S TEXT:
    //
    //   1. Its Health paragraph lists "Risk of Ruin per account" among what sits
    //      alongside Funded Status. Not built here (D134), and the screen says so
    //      in its own "Not on this screen yet" card. Removed from the list and
    //      moved to the honest section at the foot.
    //   2. Its "What's coming" section promised Eval Pipeline and Firm Comparison
    //      as V2. BOTH ARE BUILT, here and in beta — the section is stale in the
    //      source. Porting it verbatim would describe shipped features as future
    //      work. Replaced with what is genuinely absent.
    article: `
# Prop Report

Other journals treat prop accounts as independent units. The Prop Report treats them as a portfolio business — the operation you're actually running.

## What it answers

Three questions that don't live anywhere else in the product:

- **Right now**: how many accounts am I funded on, what's my combined buying power, how much cushion do I have before something blows?
- **Cash flow**: what's my payout cadence, what's a typical payout, what am I on pace to clear in the next 30 days?
- **Bottom line**: when you net every payout against every fee, am I actually running a profitable business?

The trade-centric analytics elsewhere in the product tell you *how you trade*. The Prop Report tells you *whether the operation works*.

## The two tabs

**Health** answers *am I safe right now?* **Economics** answers *has this made money?* They're separate because the honest answer to one tells you nothing about the other — you can be perfectly compliant on an operation that has never turned a profit.

### Health — Funded Status

At-a-glance portfolio composition. How many funded, how many in eval, combined buying power, combined drawdown remaining across the entire portfolio, year-to-date net.

Combined DD remaining is the most important number on this card. It's the single floor across every funded account. When it gets thin, the whole operation is at risk — not just one account.

Alongside it: target progress, the tilt signal, and a daily P&L heatmap.

### Economics — the cash position

Every fee you've paid against every payout you've received, for the life of the operation. Lifetime on purpose: a date filter would let you pick the window where you happened to be up.

ROI is stated as **dollars back per dollar spent** — "$0.84 per dollar spent" — because that's the version of the number you can act on.

Your **trade P&L is shown but deliberately kept out of the cash total**. It's account balance, not money you can spend, and folding it in is exactly what makes an operation look better than it is. Payouts are what turn balance into cash, and payouts are counted.

Most traders have no idea what this number is. The "I made $30K this year" trader who actually spent $9K on eval fees has a $21K business, not a $30K business. The Prop Report says so.

### Economics — the eval pipeline

What it actually costs to reach a funded account, including the attempts that failed: evals started / active / passed / failed / funded, cost per funded account, evals per funded, and resets used.

Pass rate counts **accounts, not attempts** — five resets on one eval is still one eval. Resets are reported separately, because resets-per-pass is the signal a pass rate hides.

### Economics — Payout Velocity

The cash-flow story. YTD payouts collected, average payout size, days between payouts (cadence), projected 30-day payout at current pace, and the **payout-to-reset ratio** — the truth-teller that asks "am I making more on payouts than I'm spending on resets?"

A payout-to-reset ratio under 2.0× is a warning sign. You're paying meaningfully into the eval-and-reset cycle relative to what you're extracting on the other side. The number won't lie.

### Economics — what you're paying for and not using

Dormant monthly accounts, flagged against the **billing date** rather than raw inactivity, so there's time to cancel before you're rebilled. The date is estimated from your last logged fee — firms don't publish their billing day. Archiving an account stops it being flagged.

## What feeds the data

The accounts you've set up plus the adjustments logged on each one. The classifier reads:

- **Account type** (combine / sim-funded / live-funded) and status (active / blown / closed) to classify accounts into Funded / Eval / Blown / Closed buckets
- **Adjustment type 'Payout'** as revenue
- **Adjustment types 'Eval fee' / 'Reset fee' / 'Activation fee'** as costs

Two implications:

1. If you've never categorized your withdrawals as 'Eval fee' / 'Reset fee' / 'Activation fee', the Economics tab will show zero costs and overstate your net. The fix is one-time: go back through your account adjustments, recategorize the fee withdrawals. Going forward, use the specific types when logging new ones.

2. Personal trading accounts are excluded from the Prop Report. The report is about prop firms specifically. Personal account stats live elsewhere.

3. **SIM, Replay and Demo accounts are excluded from Economics entirely** — including any cost recorded against them. They didn't cost real money, so a figure typed on one must never reach ROI or cost-per-funded.

## When this report lies

When the adjustment data is incomplete. The report calculates from what's logged; if you've collected payouts that aren't recorded, or paid reset fees that aren't categorized, the numbers will be wrong. The discipline is in the data entry.

## What is not on this screen yet

**Logging an adjustment.** This client cannot yet record a payout or a fee, so the Economics figures stay empty however much you have traded — they are fed by those entries, not by your trades. The Health tab's **Risk of Ruin** gauges are also not built here.
`,
  },

  'analytics.tilt-signal': {
    title: 'Tilt Signal',
    tip: 'Composite of 5 behavioral signals (observed + tagged). Banner appears at 2 fires; the goal is to surface tilt before the trader admits it.',
    // ⚠ ONE SECTION ADDED, not a correction: beta's article never says its windows
    // are measured from the trader's last trade rather than from now. That is
    // PD-29 and the banner prints the date because of it — an explainer that
    // omitted it would leave the one confusing thing on screen unexplained.
    article: `
# Tilt Signal

Tilt is a psychological state — frustration, frustration-driven aggression, urgency to "get it back." It's not directly measurable, but it has fingerprints. The Tilt Signal looks for five of them.

## The five signals

**1. Loss streak.** Three or more consecutive losers triggers it. Long streaks correlate with revenge sizing and reduced patience.

**2. Sizing creep.** Average position size in the last 24 hours is more than 1.5× the trader's all-time baseline. Quietly upsizing after a string of red is one of the most reliable tilt signatures.

**3. Frequency spike.** Trade count in the last 4 hours exceeds 2× the trader's typical rate. Rapid-fire entries suggest emotional decisions rather than setup-driven ones.

**4. Tagged mistakes.** Any FOMO or revenge flag on a trade in the last 24 hours. This is the *trader's own judgment* — when you notice and tag it, the system respects it.

**5. Grade decay.** Average self-graded quality of the last 5 trades is at least 0.5 points below the overall average. Quality drops when focus drops.

## How the score works

Each fired signal counts as 1. Total score 0–5.

- **0–1**: No banner shown. The trader is composed enough that surfacing nothing is the right call.
- **2–3**: Elevated — "Watch sizing and re-entry pace."
- **4–5**: Tilt risk — "Step away or size down."

The banner lists which signals fired so you can see the evidence, not just a verdict.

## Why observed AND tagged

Your own tags are the highest-confidence input — they're a self-acknowledged red flag. Observed signals (streak, sizing, frequency, grade) catch tilt you haven't noticed or won't admit yet.

Both together: the system respects your judgment when it's there, and surfaces what you're missing when it isn't.

## Every window is measured from your LAST TRADE

"The last 24 hours" and "the last 4 hours" are counted back from your most recent trade, not from the clock. That is deliberate — it makes the signal work on imported history — but it also means a banner built from a session five days ago would read exactly like one built this morning. So the banner prints the date and time it measured from. Check it before you act on it.

## When this signal is wrong

It can fire after a bad session that wasn't actually tilt — sometimes the market just chewed up the day. The banner is a *prompt to check yourself*, not a verdict. The right response is to look at the firing signals and decide whether they describe what's happening, not to dismiss it.
`,
  },

  // ⚠ 🔴 `analytics.risk-of-ruin` IS DELIBERATELY NOT HERE, and this note is what
  // stops somebody "finishing the job" by adding it. Beta marks a third spot on
  // the Prop Report, beside its Risk of Ruin card. Three reasons:
  //
  //   1. That card is not built here (D134), and the header's rule is that a mark
  //      whose article describes a screen that does not exist is a broken promise.
  //   2. `education.test.ts` already enforces this — no article may point at a
  //      card this client lacks, with `View Risk of Ruin` in its forbidden list.
  //   3. IT WOULD CONTRADICT THE ARTICLE ALREADY HERE. Beta's teaches a
  //      closed-form formula, ((1 − Edge) / (1 + Edge)) ^ Units;
  //      `coach.dimension.risk-of-ruin` above describes 1,500 resampled
  //      simulations, which is what this client actually runs, and a test pins
  //      that number. Two methods behind two `?` marks for one concept is worse
  //      than one mark.
  //
  // ▶ It comes back when D134 does, with the card.
};

/**
 * One entry, or null.
 *
 * ⚠ NULL RATHER THAN A THROW, and the mark renders NOTHING for it. A typo in a
 * key is a missing explainer — annoying — where a throw would blank the whole
 * tab. The old client warns to the console and returns an empty string; a test
 * here asserts every key the UI actually asks for resolves, which catches the
 * typo at the only point it can be caught mechanically.
 */
export const eduEntry = (key: string): EduEntry | null => EDUCATION[key] ?? null;

/** Does this entry have a full article behind the tooltip? */
export const eduHasArticle = (key: string): boolean => !!eduEntry(key)?.article;
